import type {
  CognitoUserInfo,
  IGetUsersParams,
  IResponseUser,
  Location,
  Role,
  UpsertUserData,
  UsersResponse
} from './types'
import type { AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'
import type { ZodIssue } from 'zod'

import { ValidationError } from '../../pages/vendors/errors/validation-error'
import $api from '../index'

function getApiErrorMessage(error: unknown): string | null {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error.message : null
  }

  const data = error.response?.data as
    | { message?: string; error?: string; code?: string }
    | string
    | undefined

  if (typeof data === 'string' && data.trim()) {
    return data.trim()
  }

  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message.trim()
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim()
    }
    if (typeof data.code === 'string' && data.code.trim()) {
      return data.code.trim()
    }
  }

  const status = error.response?.status
  const statusText = error.response?.statusText
  if (status && statusText) {
    return `${status} ${statusText}`
  }
  if (status) {
    return `HTTP ${status}`
  }

  return error.message || null
}

export const UserApi = {
  async getUserInformation(): Promise<IResponseUser> {
    return $api
      .get<IResponseUser['data']>('/auth', { validateStatus: () => true })
      .then(response => {
        return { data: response.data, status: response.status }
      })
  },

  async getUsers(param: IGetUsersParams): Promise<UsersResponse> {
    return $api.get<UsersResponse>('/users', { params: param }).then((response: AxiosResponse) => {
      return response.data
    })
  },

  async getRoles(): Promise<Role[]> {
    return $api.get<Role[]>('/roles').then((response: AxiosResponse) => {
      return response.data
    })
  },

  async getLocations(): Promise<Location[]> {
    return $api.get<Location[]>('/locations').then((response: AxiosResponse) => {
      return response.data
    })
  },

  async getUserCognitoInfo(id: string): Promise<CognitoUserInfo> {
    return $api
      .get<CognitoUserInfo>(`/users/${id}/cognito-info`)
      .then((response: AxiosResponse) => {
        return response.data
      })
  },

  async createUser(data: UpsertUserData): Promise<void> {
    try {
      await $api.post('/users', data)
    } catch (error) {
      if (
        isAxiosError<{
          issues: ZodIssue[]
          message?: string
          error?: { message?: string } | string
          code?: string
        }>(error)
      ) {
        if (error.response?.status === 422 && Array.isArray(error.response.data?.issues)) {
          throw new ValidationError(error.response.data.issues)
        }

        const message = getApiErrorMessage(error)
        throw new Error(message || 'Failed to create user')
      }

      const message = getApiErrorMessage(error)
      throw new Error(message || 'Failed to create user')
    }
  },

  async updateUser(id: string, data: UpsertUserData): Promise<void> {
    try {
      await $api.patch(`/users/${id}`, data)
    } catch (error) {
      if (isAxiosError<{ issues: ZodIssue[] }>(error) && error.response?.status === 422) {
        throw new ValidationError(error.response.data.issues)
      }
      throw new Error('Failed to update user')
    }
  },

  async resendPassword(id: string): Promise<void> {
    try {
      await $api.patch(`/users/${id}/resend-password`)
    } catch (error) {
      if (isAxiosError<{ issues: ZodIssue[] }>(error) && error.response?.status === 422) {
        throw new ValidationError(error.response.data.issues)
      }
      throw new Error('Failed to resend password')
    }
  },

  async resetPassword(id: string): Promise<void> {
    try {
      await $api.patch(`/users/${id}/reset-password`)
    } catch (error) {
      if (isAxiosError<{ issues: ZodIssue[] }>(error) && error.response?.status === 422) {
        throw new ValidationError(error.response.data.issues)
      }
      throw new Error('Failed to reset password')
    }
  }
}
